import React, { useEffect, useMemo, useState } from '../../vendor/react';
import { ArrowRight, BarChart3, Boxes, CircleDollarSign, ClipboardList, ContactRound, LayoutDashboard, Moon, PackageSearch, Search, Settings, ShoppingCart, Sparkles, Sun, X } from '../../vendor/lucide-react';
import { useNavigate } from '../../vendor/react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import { useSaas } from '../../features/saas/useSaas';
import type { CommercialFeatureId } from '../../config/commercialPlans';

type PaletteItem = {
  id: string;
  label: string;
  hint: string;
  keywords: string;
  icon: (props: { size?: number }) => JSX.Element;
  to?: string;
  question?: string;
  action?: 'theme';
  feature?: CommercialFeatureId;
};

const items: PaletteItem[] = [
  { id: 'dashboard', label: 'Resumen del negocio', hint: 'Ir al panel principal', keywords: 'inicio dashboard resumen métricas', icon: LayoutDashboard, to: '/app' },
  { id: 'pos', label: 'Punto de venta', hint: 'Iniciar o continuar una venta', keywords: 'pos cobrar ticket vender', icon: ShoppingCart, to: '/app/pos' },
  { id: 'sales', label: 'Ventas', hint: 'Consultar ventas y comprobantes', keywords: 'ventas tickets ingresos', icon: CircleDollarSign, to: '/app/ventas' },
  { id: 'products', label: 'Productos', hint: 'Administrar el catálogo', keywords: 'productos catálogo precio sku', icon: Boxes, to: '/app/productos' },
  { id: 'inventory', label: 'Inventario', hint: 'Revisar existencias y movimientos', keywords: 'inventario stock existencias reponer', icon: PackageSearch, to: '/app/inventario' },
  { id: 'orders', label: 'Pedidos', hint: 'Dar seguimiento a pedidos', keywords: 'pedidos órdenes encargos entregas', icon: ClipboardList, to: '/app/pedidos', feature: 'catalogOrders' },
  { id: 'customers', label: 'Clientes', hint: 'Buscar y administrar clientes', keywords: 'clientes contacto saldo', icon: ContactRound, to: '/app/clientes' },
  { id: 'reports', label: 'Reportes', hint: 'Analizar resultados', keywords: 'reportes utilidad gastos análisis', icon: BarChart3, to: '/app/reportes' },
  { id: 'settings', label: 'Apariencia', hint: 'Cambiar colores y modo', keywords: 'configuración tema colores apariencia', icon: Settings, to: '/app/configuracion' },
  { id: 'attention', label: '¿Qué necesita atención hoy?', hint: 'Preguntar a NEXO IA', keywords: 'ia resumen alertas prioridad hoy', icon: Sparkles, question: '¿Qué necesita atención hoy?' },
  { id: 'restock', label: 'Preparar lista de reposición', hint: 'Crear una propuesta revisable', keywords: 'ia inventario comprar reponer', icon: Sparkles, question: 'Prepara una lista de reposición' },
  { id: 'promotion', label: 'Sugerir una promoción', hint: 'Detectar productos que conviene mover', keywords: 'ia marketing oferta promoción productos', icon: Sparkles, question: '¿Qué promoción me recomiendas con mi inventario actual?' },
];

const normalize = (value: string) => value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const saas = useSaas();
  const { theme, toggleTheme } = useTheme();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);

  const results = useMemo(() => {
    const term = normalize(query.trim());
    const available = items.filter((item) => !item.feature || saas.hasFeature(item.feature));
    const filtered = term ? available.filter((item) => normalize(`${item.label} ${item.hint} ${item.keywords}`).includes(term)) : available;
    const themeItem: PaletteItem = { id: 'theme', label: `Cambiar a modo ${theme === 'light' ? 'oscuro' : 'claro'}`, hint: 'Alternar la apariencia del sistema', keywords: 'tema modo claro oscuro', icon: theme === 'light' ? Moon : Sun, action: 'theme' };
    const askItem: PaletteItem | undefined = query.trim() ? { id: 'ask-query', label: `Preguntar: “${query.trim()}”`, hint: 'Enviar esta pregunta a NEXO IA', keywords: '', icon: Sparkles, question: query.trim() } : undefined;
    return [...filtered.slice(0, 9), themeItem, ...(askItem ? [askItem] : [])];
  }, [query, theme, saas.plan?.id]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelected(0);
    const listener = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [open]);

  useEffect(() => setSelected(0), [query]);

  if (!open) return null;

  const choose = (item: PaletteItem) => {
    if (item.action === 'theme') toggleTheme();
    else if (item.question) navigate(`/app/nexo-ia?q=${encodeURIComponent(item.question)}`);
    else if (item.to) navigate(item.to);
    onClose();
  };

  return <div className="command-palette-backdrop" role="presentation" onClick={(event: any) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="command-palette" role="dialog" aria-modal="true" aria-label="Acceso rápido">
      <header><Search size={20} /><input autoFocus value={query} onChange={(event: any) => setQuery(event.target.value)} onKeyDown={(event: any) => {
        if (event.key === 'ArrowDown') { event.preventDefault(); setSelected((value) => (value + 1) % Math.max(results.length, 1)); }
        if (event.key === 'ArrowUp') { event.preventDefault(); setSelected((value) => (value - 1 + Math.max(results.length, 1)) % Math.max(results.length, 1)); }
        if (event.key === 'Enter' && results[selected]) { event.preventDefault(); choose(results[selected]); }
      }} placeholder="Busca módulos, acciones o pregunta algo…" aria-label="Buscar en NEXO" /><button className="icon-button" onClick={onClose} aria-label="Cerrar"><X size={18} /></button></header>
      <div className="command-palette__results">
        {results.map((item, index) => { const Icon = item.icon; return <button key={item.id} className={index === selected ? 'is-selected' : ''} onMouseEnter={() => setSelected(index)} onClick={() => choose(item)}><span className="command-palette__icon"><Icon size={18} /></span><span><strong>{item.label}</strong><small>{item.hint}</small></span><ArrowRight size={16} /></button>; })}
      </div>
      <footer><span><kbd>↑</kbd><kbd>↓</kbd> navegar</span><span><kbd>Enter</kbd> abrir</span><span><kbd>Esc</kbd> cerrar</span></footer>
    </section>
  </div>;
}
